import { formatDateAsAnniversary } from 'helpers/dateTimes';
import styled from 'styled-components';
import { useState } from 'react';
import { toast } from 'react-toastify';

import {
    GetOrganizationQuery,
    useGetOrganizationConsumptionQuery,
    useGetOrganizationInvoiceSimulationQuery,
    useCreateOdooInvoiceDraftMutation,
} from 'data/__generated__';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { ConsumptionSection } from './ConsumptionSection';
import { InvoiceSimulationSection } from './InvoiceSimulationSection';

// Période facturée par défaut : le mois précédent (le mois que facture le cron mensuel).
const getPreviousMonthPeriod = (): { startDate: Date; endDate: Date } => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
    return {
        startDate: new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1),
        endDate: new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0),
    };
};

const toApiDate = (date: Date): string => formatDateAsAnniversary({ dateTime: date, useNewFormat: true });

// Section « Facturation » d'une organisation : DEUX lectures de la même période, sans recouvrement —
// ce qui a été consommé (`ConsumptionSection`, observation multi-canal) et ce qu'on facturera
// (`InvoiceSimulationSection`, les lignes exactes du brouillon Odoo).
export const InvoicingDetails = ({
    organizationId,
    organization,
}: {
    organizationId: string;
    organization: GetOrganizationQuery['organization'];
}) => {
    const defaultPeriod = getPreviousMonthPeriod();

    const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(defaultPeriod.startDate);
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(defaultPeriod.endDate);

    // Période EFFECTIVEMENT affichée, commitée par « Rechercher ». Les date-pickers ne pilotent que le
    // formulaire : les deux queries ET le brouillon Odoo s'appuient sur cette seule période, sinon on peut
    // créer un brouillon sur une période jamais affichée.
    const [appliedPeriod, setAppliedPeriod] = useState({
        startDate: toApiDate(defaultPeriod.startDate),
        endDate: toApiDate(defaultPeriod.endDate),
    });

    const {
        data: consumptionData,
        loading: consumptionLoading,
        error: consumptionError,
        refetch: refetchConsumption,
    } = useGetOrganizationConsumptionQuery({
        variables: { organizationId, ...appliedPeriod },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const {
        data: simulationData,
        loading: simulationLoading,
        error: simulationError,
        refetch: refetchSimulation,
    } = useGetOrganizationInvoiceSimulationQuery({
        variables: { organizationId, ...appliedPeriod },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const [createOdooInvoiceDraft, { loading: creatingInvoice }] = useCreateOdooInvoiceDraftMutation();

    // Commite la période du formulaire : le changement de variables relance les queries. Une date vidée garde
    // la borne actuelle (pas d'appel sur une période incomplète), et une période inchangée rejoue quand même
    // les deux lectures — le bouton reste un rafraîchissement.
    const handleSearch = () => {
        const searchedPeriod = {
            startDate: selectedStartDate ? toApiDate(selectedStartDate) : appliedPeriod.startDate,
            endDate: selectedEndDate ? toApiDate(selectedEndDate) : appliedPeriod.endDate,
        };
        if (searchedPeriod.startDate === appliedPeriod.startDate && searchedPeriod.endDate === appliedPeriod.endDate) {
            refetchConsumption();
            refetchSimulation();
            return;
        }
        setAppliedPeriod(searchedPeriod);
    };

    const handleCreateOdooInvoiceDraft = async () => {
        try {
            const { data } = await createOdooInvoiceDraft({
                variables: {
                    organizationId,
                    ...appliedPeriod,
                },
            });

            if (data?.createOdooInvoiceDraft?.error) {
                toast.error(data.createOdooInvoiceDraft.error);
            } else if (data?.createOdooInvoiceDraft?.invoiceId) {
                toast.success(
                    `Brouillon de facture Odoo créé / modifié avec succès ! ID: ${data.createOdooInvoiceDraft.invoiceId}`,
                );
            } else {
                toast.error("Aucune facture n'a été créée ou modifiée");
            }
        } catch (err) {
            toast.error('Une erreur est survenue lors de la création du brouillon de facture');
        }
    };

    return (
        <Container>
            <PeriodSelection>
                <TotemDatePicker
                    label="Date de début"
                    selected={selectedStartDate}
                    onChange={setSelectedStartDate}
                    shouldAllowPastDates
                />
                <TotemDatePicker
                    label="Date de fin"
                    selected={selectedEndDate}
                    onChange={setSelectedEndDate}
                    shouldAllowPastDates
                />
                <TotemPrimaryButton type="button" onClick={handleSearch}>
                    <span>Rechercher</span>
                </TotemPrimaryButton>
            </PeriodSelection>
            <ConsumptionSection
                consumption={consumptionData?.getOrganizationConsumption}
                // Montants par canal : ceux de la facture, jamais recalculés côté conso.
                invoiceChannels={simulationData?.getOrganizationInvoiceSimulation.totalsByCategory}
                loading={consumptionLoading}
                error={consumptionError}
            />
            <InvoiceSimulationSection
                simulation={simulationData?.getOrganizationInvoiceSimulation}
                loading={simulationLoading}
                error={simulationError}
                organization={organization}
                creatingInvoice={creatingInvoice}
                handleCreateOdooInvoiceDraft={handleCreateOdooInvoiceDraft}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px;
    overflow-y: auto;
`;

const PeriodSelection = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    align-items: flex-end;
    margin-bottom: 24px;
`;
